import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { AppSession } from "./useSession.js";

type AuthEntryMode = "login" | "forgot-password" | "reset-password" | "register";

function resolveAuthEntryMode(pathname: string): AuthEntryMode | null {
  if (pathname === "/login") return "login";
  if (pathname === "/forgot-password") return "forgot-password";
  if (pathname === "/reset-password") return "reset-password";
  if (pathname === "/register") return "register";
  return null;
}

export function LandingScreen({ onLoginClick }: { onLoginClick: () => void }) {
  return (
    <main className="fd-shell fd-shell--landing fd-v2-landing-shell">
      <nav className="fd-landing-nav">
        <a className="fd-landing-brand" href="/" onClick={(event) => event.preventDefault()}>
          <div className="fd-landing-logo-icon">F</div>
          <div className="fd-v2-landing-logo-row">
            <span className="fd-landing-logo">FirstDesk</span>
            <span className="fd-landing-logo-dot">.nl</span>
          </div>
        </a>
        <div className="fd-landing-nav-actions">
          <button className="fd-button fd-button--ghost fd-button--small" type="button" onClick={onLoginClick}>Inloggen</button>
          <a className="fd-button fd-button--primary fd-button--small" href="/register">Registreren</a>
        </div>
      </nav>

      <section className="fd-landing-hero-section">
        <div className="fd-landing-hero">
          <div className="fd-landing-hero-left">
            <div className="fd-live-badge">
              <span className="fd-live-badge-dot">
                <span className="fd-live-badge-dot-inner" />
                <span className="fd-live-badge-dot-core" />
              </span>
              Live Simulation Platform
            </div>
            <h1 className="fd-hero-title">
              Dit is waar je <span className="fd-title-accent">leert</span> werken <span>FirstDesk</span>.
            </h1>
            <p className="fd-hero-lead">
              Start direct je simulatie, werk een ticket volledig uit en lever je rapportage in vanuit dezelfde omgeving.
              Een alles-in-een leerervaring voor de ICT-professional van morgen.
            </p>
            <div className="fd-hero-actions">
              <button className="fd-button--hero-primary" type="button" onClick={onLoginClick}>Start simulatie</button>
              <a className="fd-button--hero-outline" href="/register">Registreren</a>
            </div>
            <div className="fd-chip-row">
              <span className="fd-chip">Tickets</span>
              <span className="fd-chip">Scenario's</span>
              <span className="fd-chip">Docentreview</span>
              <span className="fd-chip">Windows Server</span>
            </div>
          </div>

          <div className="fd-landing-hero-right">
            <div className="fd-hero-glow-blob" />
            <div className="fd-hero-mock-frame">
              <div className="fd-hero-mock-inner">
                <div className="fd-hero-mock-titlebar">
                  <span className="fd-hero-mock-titlebar-dot" />
                  <span className="fd-hero-mock-titlebar-dot" />
                  <span className="fd-hero-mock-titlebar-dot" />
                  <span className="fd-hero-mock-titlebar-label">Ticketsysteem - simulatie</span>
                </div>
                <div className="fd-hero-mock-content">
                  <div className="fd-hero-mock-panel">
                    <span className="fd-hero-mock-label">Open ticket</span>
                    <span className="fd-hero-mock-value">Gebruiker kan niet aanmelden op werkplek</span>
                  </div>
                  <div className="fd-hero-mock-panel">
                    <span className="fd-hero-mock-label">Werkstroom</span>
                    <span className="fd-hero-mock-value">Onderzoek &gt; Oplossen &gt; Verslag indienen</span>
                  </div>
                  <div className="fd-hero-mock-panel fd-hero-mock-panel--accent">
                    <span className="fd-hero-mock-label">Simulator actief</span>
                    <span className="fd-hero-mock-value">Windows Server praktijkomgeving</span>
                  </div>
                  <div className="fd-hero-mock-glass">
                    <div className="fd-hero-mock-glass-inner">
                      <div className="fd-hero-mock-avatar">
                        <span className="material-symbols-outlined">dns</span>
                      </div>
                      <div>
                        <span className="fd-hero-mock-meta-title">Server Management Simulation</span>
                        <span className="fd-hero-mock-meta-sub">Status: Active - Scenario #4</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="fd-landing-features-section">
        <div className="fd-landing-features">
          <div className="fd-landing-features-header">
            <h2>Kracht voor iedereen</h2>
            <p>Geoptimaliseerd voor elke rol binnen het onderwijsproces. Van student tot systeembeheerder.</p>
          </div>
          <div className="fd-feature-grid">
            <div className="fd-feature-card">
              <div className="fd-feature-icon"><span className="material-symbols-outlined">school</span></div>
              <h3>Voor de Student</h3>
              <p>Oefen in een realistische ICT-omgeving zonder risico. Werk aan tickets, configureer servers en rond je verslag af.</p>
            </div>
            <div className="fd-feature-card">
              <div className="fd-feature-icon fd-feature-icon--tertiary"><span className="material-symbols-outlined">analytics</span></div>
              <h3>Voor de Docent</h3>
              <p>Beheer opdrachten, volg voortgang in real-time en beoordeel rapportages centraal.</p>
            </div>
            <div className="fd-feature-card">
              <div className="fd-feature-icon"><span className="material-symbols-outlined">corporate_fare</span></div>
              <h3>Voor de Instelling</h3>
              <p>Een schaalbaar platform dat fysieke labs ontlast en studenten 24/7 toegang geeft.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="fd-landing-editorial">
        <div className="fd-landing-editorial-inner">
          <div className="fd-editorial-visual" aria-hidden="true">
            <div className="fd-editorial-bg-blob" />
            <div className="fd-editorial-frame">
              <div className="fd-editorial-frame-inner">
                {[
                  ["confirmation_number", "Ticket aangemaakt", "Gebruiker meldt DNS-probleem", "Nieuw"],
                  ["desktop_windows", "Simulator gestart", "Windows Server en clientomgeving actief", "Actief"],
                  ["check_circle", "Doel gecontroleerd", "DNS-record en clientresolutie gevalideerd", "Geslaagd"],
                  ["rate_review", "Verslag klaar", "Docentfeedback beschikbaar in dashboard", "Review"]
                ].map(([icon, title, subtitle, status]) => (
                  <div className="fd-editorial-mock-row" key={title}>
                    <span className="fd-editorial-mock-icon"><span className="material-symbols-outlined">{icon}</span></span>
                    <span>
                      <span className="fd-editorial-mock-text-title">{title}</span>
                      <span className="fd-editorial-mock-text-sub">{subtitle}</span>
                    </span>
                    <span className="fd-editorial-mock-status">{status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="fd-editorial-content">
            <h2>Van melding naar bewijs in <span>een leerflow</span>.</h2>
            <ul className="fd-check-list">
              <li className="fd-check-item">
                <span className="fd-check-icon"><span className="material-symbols-outlined">done</span></span>
                <div>
                  <h4>Realistische servicedesk-tickets</h4>
                  <p>Studenten werken vanuit herkenbare meldingen en bouwen stapsgewijs aan diagnose, oplossing en rapportage.</p>
                </div>
              </li>
              <li className="fd-check-item">
                <span className="fd-check-icon"><span className="material-symbols-outlined">done</span></span>
                <div>
                  <h4>Docentcontrole zonder spreadsheetwerk</h4>
                  <p>Voortgang, gesloten tickets en feedbackmomenten blijven gekoppeld aan klasgroepen en individuele pogingen.</p>
                </div>
              </li>
              <li className="fd-check-item">
                <span className="fd-check-icon"><span className="material-symbols-outlined">done</span></span>
                <div>
                  <h4>Veilig oefenen in de browser</h4>
                  <p>De simulator houdt technische doelen server-side afgeschermd en toont studenten alleen wat zij nodig hebben.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="fd-landing-cta-section">
        <div className="fd-landing-cta-inner">
          <span className="fd-landing-cta-rocket" aria-hidden="true"><span className="material-symbols-outlined">rocket_launch</span></span>
          <h2>Start met FirstDesk</h2>
          <p><strong>Maak een account aan</strong> of log in om direct naar je tickets, groepen en simulatoromgeving te gaan.</p>
          <button className="fd-button--cta-white" type="button" onClick={onLoginClick}>
            Inloggen
            <span className="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
      </section>

      <footer className="fd-landing-footer">
        <div className="fd-landing-footer-inner">
          <div className="fd-landing-footer-brand">
            <span className="fd-landing-footer-logo">FirstDesk.nl</span>
            <span className="fd-landing-footer-copy">ICT praktijkleren in een veilige simulatieomgeving</span>
          </div>
          <nav className="fd-landing-footer-nav" aria-label="Publieke links">
            <button className="fd-link-button" type="button" onClick={onLoginClick}>Inloggen</button>
            <a href="/register">Registreren</a>
            <a href="/forgot-password">Wachtwoord vergeten</a>
          </nav>
        </div>
      </footer>
    </main>
  );
}

export function AuthBootstrapScreen() {
  return (
    <main className="fd-auth-shell fd-v2-auth-shell">
      <div className="fd-auth-bg-blobs" aria-hidden="true">
        <div className="fd-auth-bg-blob fd-auth-bg-blob--tl" />
        <div className="fd-auth-bg-blob fd-auth-bg-blob--br" />
      </div>

      <div className="fd-auth-container">
        <div className="fd-auth-brand-header">
          <span className="fd-auth-brand-name">FirstDesk.nl</span>
          <span className="fd-auth-brand-sub">Dit is waar je leert werken.</span>
        </div>

        <div className="fd-auth-card">
          <div className="fd-auth-card-accent" />
          <div className="fd-auth-card-body">
            <header className="fd-auth-card-header">
              <h2>Omgeving laden</h2>
              <p>Je sessie wordt gecontroleerd.</p>
            </header>
          </div>
        </div>
      </div>
    </main>
  );
}

function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const response = await fetch("/api/v2/auth/forgot-password", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          recaptchaToken: "dev-recaptcha-token-placeholder"
        })
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) {
        setError(payload.error === "recaptcha_verification_failed" ? "reCAPTCHA verificatie is mislukt." : "Aanvraag versturen is niet gelukt.");
        return;
      }
      setSuccess("Als dit e-mailadres bestaat, ontvang je binnen enkele minuten een resetlink.");
    } catch {
      setError("De FirstDesk omgeving is niet bereikbaar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="fd-auth-shell fd-v2-auth-shell">
      <div className="fd-auth-bg-blobs" aria-hidden="true">
        <div className="fd-auth-bg-blob fd-auth-bg-blob--tl" />
        <div className="fd-auth-bg-blob fd-auth-bg-blob--br" />
      </div>

      <div className="fd-auth-container">
        <div className="fd-auth-brand-header">
          <span className="fd-auth-brand-name">FirstDesk.nl</span>
          <span className="fd-auth-brand-sub">Dit is waar je leert werken.</span>
        </div>

        <div className="fd-auth-card">
          <div className="fd-auth-card-accent" />
          <div className="fd-auth-card-body">
            <header className="fd-auth-card-header">
              <h2>Wachtwoord vergeten</h2>
              <p>Vraag een resetlink aan via je e-mailadres.</p>
            </header>
            {error ? <div className="fd-auth-error-banner" role="alert">{error}</div> : null}
            {success ? <div className="fd-auth-support-copy">{success}</div> : null}
            <form className="fd-auth-form" onSubmit={submit}>
              <input className="fd-auth-input" placeholder="naam@instituut.nl" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <button className="fd-auth-submit" disabled={submitting} type="submit">
                {submitting ? "Aanvragen..." : "Resetlink versturen"}
              </button>
            </form>

            <div className="fd-auth-support-actions">
              <a className="fd-auth-submit fd-auth-submit--link" href="/login">
                Terug naar inloggen
                <span className="material-symbols-outlined">arrow_back</span>
              </a>
              <a className="fd-btn-secondary-pill" href="https://firstdesk.nl" rel="noreferrer">
                Open live omgeving
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function ResetPasswordScreen() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!token) {
      setError("Resetlink is ongeldig.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Wachtwoorden komen niet overeen.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("/api/v2/auth/reset-password", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, password })
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) {
        setError(payload.error === "invalid_or_expired_reset_token" ? "Resetlink is ongeldig of verlopen." : "Wachtwoord resetten is niet gelukt.");
        return;
      }
      setSuccess("Je wachtwoord is aangepast. Je kunt nu inloggen.");
      setPassword("");
      setConfirmPassword("");
    } catch {
      setError("De FirstDesk omgeving is niet bereikbaar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="fd-auth-shell fd-v2-auth-shell">
      <div className="fd-auth-bg-blobs" aria-hidden="true">
        <div className="fd-auth-bg-blob fd-auth-bg-blob--tl" />
        <div className="fd-auth-bg-blob fd-auth-bg-blob--br" />
      </div>
      <div className="fd-auth-container">
        <div className="fd-auth-brand-header">
          <span className="fd-auth-brand-name">FirstDesk.nl</span>
          <span className="fd-auth-brand-sub">Wachtwoord opnieuw instellen</span>
        </div>
        <div className="fd-auth-card">
          <div className="fd-auth-card-accent" />
          <div className="fd-auth-card-body">
            <header className="fd-auth-card-header">
              <h2>Nieuw wachtwoord</h2>
              <p>Kies een nieuw wachtwoord voor je account.</p>
            </header>
            {error ? <div className="fd-auth-error-banner" role="alert">{error}</div> : null}
            {success ? <div className="fd-auth-support-copy">{success}</div> : null}
            <form className="fd-auth-form" onSubmit={submit}>
              <input className="fd-auth-input" placeholder="Nieuw wachtwoord (min. 10 tekens)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required />
              <input className="fd-auth-input" placeholder="Herhaal wachtwoord" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} minLength={10} required />
              <button className="fd-auth-submit" disabled={submitting} type="submit">
                {submitting ? "Opslaan..." : "Wachtwoord opslaan"}
              </button>
            </form>
            <footer className="fd-auth-card-footer">
              <a className="fd-btn-secondary-pill" href="/login">Terug naar inloggen</a>
            </footer>
          </div>
        </div>
      </div>
    </main>
  );
}

interface RegisterSchoolOption {
  code: string;
  name: string;
}

function RegisterScreen() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolCode, setSchoolCode] = useState("TALLAND");
  const [schools, setSchools] = useState<RegisterSchoolOption[]>([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const response = await fetch("/api/v2/auth/register/options", { credentials: "include" });
        if (!response.ok) return;
        const payload = await response.json() as { schools?: RegisterSchoolOption[] };
        if (!mounted) return;
        setSchools(payload.schools ?? []);
        if (payload.schools?.[0]?.code) setSchoolCode(payload.schools[0].code);
      } catch {
        if (mounted) setSchools([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const response = await fetch("/api/v2/auth/register", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          fullName,
          email,
          password,
          schoolCode,
          recaptchaToken: "dev-recaptcha-token-placeholder"
        })
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) {
        if (payload.error === "email_domain_not_allowed") {
          setError("Dit emaildomein hoort niet bij de gekozen school.");
        } else if (payload.error === "email_already_registered") {
          setError("Dit emailadres is al geregistreerd.");
        } else if (payload.error === "recaptcha_verification_failed") {
          setError("reCAPTCHA verificatie is mislukt.");
        } else {
          setError("Registreren is niet gelukt.");
        }
        return;
      }
      setSuccess("Account aangemaakt. Je kunt nu inloggen.");
      setPassword("");
    } catch {
      setError("De FirstDesk omgeving is niet bereikbaar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="fd-auth-shell fd-v2-auth-shell">
      <div className="fd-auth-bg-blobs" aria-hidden="true">
        <div className="fd-auth-bg-blob fd-auth-bg-blob--tl" />
        <div className="fd-auth-bg-blob fd-auth-bg-blob--br" />
      </div>
      <div className="fd-auth-container">
        <div className="fd-auth-brand-header">
          <span className="fd-auth-brand-name">FirstDesk.nl</span>
          <span className="fd-auth-brand-sub">Studentaccount aanmaken</span>
        </div>
        <div className="fd-auth-card">
          <div className="fd-auth-card-accent" />
          <div className="fd-auth-card-body">
            <header className="fd-auth-card-header">
              <h2>Registreren</h2>
              <p>Maak je account aan met je schooldomein.</p>
            </header>
            {error ? <div className="fd-auth-error-banner" role="alert">{error}</div> : null}
            {success ? <div className="fd-auth-support-copy">{success}</div> : null}
            <form className="fd-auth-form" onSubmit={submit}>
              <input className="fd-auth-input" placeholder="Volledige naam" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              <input className="fd-auth-input" placeholder="naam@school.nl" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input className="fd-auth-input" placeholder="Wachtwoord (min. 10 tekens)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required />
              <select className="fd-auth-input" value={schoolCode} onChange={(e) => setSchoolCode(e.target.value)} required>
                {schools.map((school) => (
                  <option key={school.code} value={school.code}>{school.name}</option>
                ))}
              </select>
              <button className="fd-auth-submit" disabled={submitting} type="submit">
                {submitting ? "Registreren..." : "Account aanmaken"}
              </button>
            </form>
            <footer className="fd-auth-card-footer">
              <a className="fd-btn-secondary-pill" href="/login">Terug naar inloggen</a>
            </footer>
          </div>
        </div>
      </div>
    </main>
  );
}

export function LoginScreen({ onLogin }: { onLogin: (session: AppSession) => void }) {
  const entryMode = resolveAuthEntryMode(window.location.pathname) ?? "login";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const response = await fetch("/api/v2/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password, remember })
      });

      const payload = await response.json() as { session?: AppSession; error?: string };

      if (!response.ok || !payload.session) {
        setError(payload.error === "invalid_credentials" ? "Ongeldige inloggegevens." : "Inloggen is niet gelukt.");
        return;
      }

      onLogin(payload.session);
    } catch {
      setError("De FirstDesk omgeving is niet bereikbaar.");
    } finally {
      setSubmitting(false);
    }
  }

  if (entryMode !== "login") {
    if (entryMode === "register") {
      return <RegisterScreen />;
    }
    if (entryMode === "forgot-password") {
      return <ForgotPasswordScreen />;
    }
    return <ResetPasswordScreen />;
  }

  return (
    <main className="fd-auth-shell fd-v2-auth-shell">
      <div className="fd-auth-bg-blobs" aria-hidden="true">
        <div className="fd-auth-bg-blob fd-auth-bg-blob--tl" />
        <div className="fd-auth-bg-blob fd-auth-bg-blob--br" />
      </div>

      <div className="fd-auth-container">
        <div className="fd-auth-brand-header">
          <span className="fd-auth-brand-name">FirstDesk.nl</span>
          <span className="fd-auth-brand-sub">Dit is waar je leert werken.</span>
        </div>

        <div className="fd-auth-card">
          <div className="fd-auth-card-accent" />
          <div className="fd-auth-card-body">
            <header className="fd-auth-card-header">
              <h2>Welkom terug</h2>
              <p>Voer je inloggegevens in om verder te gaan.</p>
            </header>

            {error ? (
              <div className="fd-auth-error-banner" role="alert">
                <span className="material-symbols-outlined fd-auth-error-icon">error</span>
                <div>
                  <span className="fd-auth-error-title">Toegang geweigerd</span>
                  <span className="fd-auth-error-desc">{error}</span>
                </div>
              </div>
            ) : null}

            <form className="fd-auth-form" onSubmit={submit}>
              <div className="fd-field-group">
                <div className="fd-field-label">
                  <span className="fd-field-label-text">Email adres</span>
                </div>
                <div className="fd-input-icon-wrap">
                  <div className="fd-input-icon-prefix">
                    <span className="material-symbols-outlined">mail</span>
                  </div>
                  <input
                    autoComplete="email"
                    className="fd-auth-input fd-auth-input--with-icon-left"
                    id="email"
                    name="email"
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="naam@instituut.nl"
                    required
                    type="email"
                    value={email}
                  />
                </div>
              </div>

              <div className="fd-field-group">
                <div className="fd-field-label">
                  <span className="fd-field-label-text">Wachtwoord</span>
                  <a className="fd-field-label-link" href="/forgot-password">Wachtwoord vergeten?</a>
                </div>
                <div className="fd-input-icon-wrap">
                  <div className="fd-input-icon-prefix">
                    <span className="material-symbols-outlined">lock</span>
                  </div>
                  <input
                    autoComplete="current-password"
                    className="fd-auth-input fd-auth-input--with-icon-left fd-auth-input--with-icon-right"
                    id="password"
                    name="password"
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="********"
                    required
                    type={showPassword ? "text" : "password"}
                    value={password}
                  />
                  <button
                    aria-label={showPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                    className="fd-input-icon-suffix"
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    <span className="material-symbols-outlined">{showPassword ? "visibility_off" : "visibility"}</span>
                  </button>
                </div>
              </div>

              <div className="fd-auth-check-row">
                <input
                  checked={remember}
                  id="remember"
                  name="remember"
                  type="checkbox"
                  onChange={(event) => setRemember(event.target.checked)}
                />
                <label htmlFor="remember">Blijf ingelogd op dit apparaat</label>
              </div>

              <button className="fd-auth-submit" disabled={submitting} type="submit">
                {submitting ? "Inloggen..." : "Naar dashboard"}
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </form>

            <footer className="fd-auth-card-footer">
              <p>Nog geen account bij jouw instelling?</p>
              <a className="fd-btn-secondary-pill" href="/register">Studentaccount aanmaken</a>
            </footer>
          </div>
        </div>

        <div className="fd-auth-meta-row">
          <div className="fd-auth-meta-status">
            <span className="fd-auth-meta-dot" />
            <span className="fd-auth-meta-text">System Operational</span>
          </div>
          <span className="fd-auth-meta-text">v2 parallelomgeving</span>
        </div>
      </div>
    </main>
  );
}
